import React, { useState, useMemo } from 'react';
import { Plus, Search, Edit, Lock, Power, Building2, Mail, Phone, User, Download, RefreshCw } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { Modal } from '../../components/ui/Modal';
import { pdfService } from '../../services';
import { useSchools, useSchoolMutations } from '../../hooks/useConvex';
import { School } from '../../types';
import { generateRandomPassword } from '../../utils/auth.utils';
import { useAuth } from '../../contexts/AuthContext';
import { Id } from '../../../convex/_generated/dataModel';

export const SchoolsPage: React.FC = () => {
    const { adminRole, assignedSchoolId } = useAuth();
    const schoolsData = useSchools();
    const { create, update, toggleActive } = useSchoolMutations();
    const loading = schoolsData === undefined;

    const [searchQuery, setSearchQuery] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingSchool, setEditingSchool] = useState<School | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const [newSchool, setNewSchool] = useState({
        school_name: '',
        address: '',
        principal_name: '',
        principal_contact: '',
        email: '',
        password: generateRandomPassword(),
    });

    // Transform Convex data to School type and filter based on role
    const schools = useMemo(() => {
        if (!schoolsData) return [];
        let data = schoolsData.map((s: Record<string, unknown>) => ({
            ...s,
            school_id: s._id,
        })) as School[];

        if (adminRole === 'escort' && assignedSchoolId) {
            data = data.filter((s: School) => s.school_id === assignedSchoolId);
        }

        return data;
    }, [schoolsData, adminRole, assignedSchoolId]);

    const filteredSchools = useMemo(() => {
        const query = searchQuery.toLowerCase();
        return schools.filter(school =>
            school.school_name.toLowerCase().includes(query) ||
            school.principal_name?.toLowerCase().includes(query) ||
            school.email?.toLowerCase().includes(query)
        );
    }, [searchQuery, schools]);

    const handleCreateSchool = async () => {
        try {
            setActionLoading(true);
            await create({
                schoolName: newSchool.school_name,
                branchName: '',
                createdBy: '',
            });
            alert(`School created!\nEmail: ${newSchool.email}\n\nPlease save these credentials.`);
            setIsModalOpen(false);
            setNewSchool({
                school_name: '',
                address: '',
                principal_name: '',
                principal_contact: '',
                email: '',
                password: generateRandomPassword(),
            });
        } catch (error: any) {
            alert(error.message || 'Failed to create school');
        } finally {
            setActionLoading(false);
        }
    };

    const handleToggleStatus = async (schoolId: string, currentStatus: boolean) => {
        if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this school?`)) return;

        try {
            await toggleActive({ schoolId: schoolId as Id<'schools'>, isActive: !currentStatus });
        } catch (error) {
            alert('Failed to update school status');
        }
    };

    const handleEditSchool = (school: School) => {
        setEditingSchool(school);
        setIsEditModalOpen(true);
    };

    const handleUpdateSchool = async () => {
        if (!editingSchool) return;

        try {
            setActionLoading(true);
            await update({
                schoolId: editingSchool.school_id as Id<'schools'>,
                schoolName: editingSchool.school_name,
                address: editingSchool.address,
                principalName: editingSchool.principal_name,
                principalContact: editingSchool.principal_contact,
                email: editingSchool.email,
            });
            setIsEditModalOpen(false);
            setEditingSchool(null);
        } catch (error: any) {
            alert(error.message || 'Failed to update school');
        } finally {
            setActionLoading(false);
        }
    };

    const handleExportPDF = () => {
        const data = filteredSchools.map(school => [
            school.school_name + (school.branch_name ? ` (${school.branch_name})` : ''),
            school.team_code,
            school.principal_name,
            school.principal_contact,
            school.email,
            school.is_active ? 'Active' : 'Inactive'
        ]);

        pdfService.generatePDF({
            title: 'Registered Schools',
            filename: 'schools_list',
            columns: ['School Name', 'Team Code', 'Principal', 'Contact', 'Email', 'Status'],
            data: data,
            orientation: 'landscape',
        });
    };

    return (
        <div className="space-y-8">
            {/* Page Header */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand/[0.08] border border-brand/[0.12] flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-brand" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-display font-bold text-white tracking-tight">Schools</h1>
                            <p className="text-sm text-white/30 mt-0.5">{schools.length} registered schools</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="secondary" onClick={handleExportPDF}>
                            <Download className="w-4 h-4 mr-2" />
                            Export
                        </Button>
                        {(adminRole === 'supreme_admin' || adminRole === 'admin') && (
                            <Button variant="primary" onClick={() => setIsModalOpen(true)}>
                                <Plus className="w-4 h-4 mr-2" />
                                Add School
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25 w-4 h-4" />
                <input
                    type="text"
                    placeholder="Search schools by name, principal, or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] text-white text-sm placeholder:text-white/25 focus:border-brand/30 focus:outline-none transition-all"
                />
            </div>

            {/* Schools List */}
            <div className="grid gap-3">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader size="lg" />
                    </div>
                ) : filteredSchools.length === 0 ? (
                    <div className="text-center py-12">
                        <Building2 className="w-12 h-12 mx-auto mb-4 text-white/10" />
                        <p className="text-lg font-medium text-white/30">No schools found</p>
                        <p className="text-sm text-white/20 mt-1">Try adjusting your search or add a new school.</p>
                    </div>
                ) : (
                    filteredSchools.map((school) => (
                        <Card key={school.school_id} hoverEffect>
                            <div className="p-5">
                                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                    <div className="flex-1 space-y-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <h3 className="text-lg font-semibold text-white">
                                                    {school.school_name}
                                                    {school.branch_name && (
                                                        <span className="text-white/30 ml-2 font-normal text-base">
                                                            {school.branch_name}
                                                        </span>
                                                    )}
                                                </h3>
                                            </div>
                                            <Badge variant={school.is_active ? 'success' : 'error'} size="sm">
                                                {school.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <div className="flex items-center gap-2 text-xs text-white/35">
                                                <User className="w-3.5 h-3.5" />
                                                <span>{school.principal_name}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-white/35">
                                                <Phone className="w-3.5 h-3.5" />
                                                <span>{school.principal_contact}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-white/35">
                                                <Mail className="w-3.5 h-3.5" />
                                                <span className="truncate">{school.email}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <span className="font-mono text-sm text-brand bg-brand/[0.05] px-3 py-1.5 rounded-lg border border-brand/[0.1]">
                                                {school.team_code}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {(adminRole === 'supreme_admin' || adminRole === 'admin') && (
                                            <>
                                                <button
                                                    onClick={() => handleEditSchool(school)}
                                                    className="h-9 w-9 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
                                                    title="Edit Details"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    className="h-9 w-9 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
                                                    title="Reset Password"
                                                >
                                                    <Lock className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleToggleStatus(school.school_id, school.is_active)}
                                                    className={`h-9 w-9 rounded-lg flex items-center justify-center transition-colors ${
                                                        school.is_active
                                                            ? 'text-white/40 hover:text-red-400 hover:bg-red-500/10'
                                                            : 'text-white/40 hover:text-green-400 hover:bg-green-500/10'
                                                    }`}
                                                    title={school.is_active ? 'Deactivate' : 'Activate'}
                                                >
                                                    <Power className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))
                )}
            </div>

            {/* Add School Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Add New School"
                size="lg"
            >
                <div className="space-y-4">
                    <Input
                        label="School Name"
                        value={newSchool.school_name}
                        onChange={(e) => setNewSchool({ ...newSchool, school_name: e.target.value })}
                        placeholder="Enter school name"
                        fullWidth
                    />
                    <Input
                        label="Address"
                        value={newSchool.address}
                        onChange={(e) => setNewSchool({ ...newSchool, address: e.target.value })}
                        placeholder="Enter full address"
                        fullWidth
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Input
                            label="Principal Name"
                            value={newSchool.principal_name}
                            onChange={(e) => setNewSchool({ ...newSchool, principal_name: e.target.value })}
                            placeholder="Enter principal's name"
                        />
                        <Input
                            label="Principal Contact"
                            value={newSchool.principal_contact}
                            onChange={(e) => setNewSchool({ ...newSchool, principal_contact: e.target.value })}
                            placeholder="Enter contact number"
                        />
                    </div>
                    <Input
                        label="Email (for login)"
                        type="email"
                        value={newSchool.email}
                        onChange={(e) => setNewSchool({ ...newSchool, email: e.target.value })}
                        placeholder="school@example.com"
                        fullWidth
                    />

                    <div className="bg-white/[0.03] rounded-xl p-4 border border-white/[0.04]">
                        <label className="block text-sm font-medium text-white/60 mb-2">Generated Password</label>
                        <div className="flex items-center gap-3">
                            <code className="flex-1 px-3 py-2 rounded-lg bg-black/50 text-brand font-mono text-base border border-brand/20">
                                {newSchool.password}
                            </code>
                            <button
                                onClick={() => setNewSchool({ ...newSchool, password: generateRandomPassword() })}
                                className="h-9 w-9 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        </div>
                        <p className="text-xs text-yellow-500/80 mt-2">
                            Make sure to copy this password. It will be shown only once.
                        </p>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.04]">
                        <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleCreateSchool}
                            isLoading={actionLoading}
                            disabled={!newSchool.school_name || !newSchool.email}
                        >
                            Create School
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Edit School Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditingSchool(null);
                }}
                title="Edit School"
                size="lg"
            >
                {editingSchool && (
                    <div className="space-y-4">
                        <Input
                            label="School Name"
                            value={editingSchool.school_name}
                            onChange={(e) => setEditingSchool({ ...editingSchool, school_name: e.target.value })}
                            fullWidth
                        />
                        <Input
                            label="Address"
                            value={editingSchool.address || ''}
                            onChange={(e) => setEditingSchool({ ...editingSchool, address: e.target.value })}
                            fullWidth
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                                label="Principal Name"
                                value={editingSchool.principal_name || ''}
                                onChange={(e) => setEditingSchool({ ...editingSchool, principal_name: e.target.value })}
                            />
                            <Input
                                label="Principal Contact"
                                value={editingSchool.principal_contact || ''}
                                onChange={(e) => setEditingSchool({ ...editingSchool, principal_contact: e.target.value })}
                            />
                        </div>
                        <Input
                            label="Principal Email"
                            type="email"
                            value={editingSchool.email || ''}
                            onChange={(e) => setEditingSchool({ ...editingSchool, email: e.target.value })}
                            fullWidth
                        />

                        <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.04]">
                            <Button variant="ghost" onClick={() => {
                                setIsEditModalOpen(false);
                                setEditingSchool(null);
                            }}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleUpdateSchool}
                                isLoading={actionLoading}
                            >
                                Update School
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};
